import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { getCookieConsent } from './CookieConsent';
import { Download, X, Share, PlusSquare } from 'lucide-react';

const DISMISS_KEY = 'pwa-install-dismissed-at';
const DISMISS_DAYS = 30;

const isStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;

const isIos = () =>
  /iPad|iPhone|iPod/.test(navigator.userAgent) ||
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

const recentlyDismissed = () => {
  const ts = localStorage.getItem(DISMISS_KEY);
  if (!ts) return false;
  return Date.now() - Number(ts) < DISMISS_DAYS * 24 * 60 * 60 * 1000;
};

export const InstallAppPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showIosGuide, setShowIosGuide] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isStandalone() || recentlyDismissed()) return;

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);

    // iOS Safari has no beforeinstallprompt — show manual guide instead
    let iosTimer;
    if (isIos()) {
      iosTimer = setTimeout(() => setShowIosGuide(true), 3000);
    }

    // Wait until cookie consent is answered so the banners never overlap
    const visTimer = setInterval(() => {
      if (getCookieConsent()) {
        setVisible(true);
        clearInterval(visTimer);
      }
    }, 1000);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      if (iosTimer) clearTimeout(iosTimer);
      clearInterval(visTimer);
    };
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
  };

  const install = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setVisible(false);
    } else {
      dismiss();
    }
    setDeferredPrompt(null);
  };

  if (!visible || (!deferredPrompt && !showIosGuide)) return null;

  return (
    <div
      className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:max-w-sm z-50 bg-slate-800 border border-slate-600 rounded-xl shadow-2xl shadow-black/50 p-4 animate-in slide-in-from-bottom-4"
      data-testid="install-app-prompt"
    >
      <div className="flex items-start gap-3">
        <img src="/icon-192.png" alt="" className="h-10 w-10 rounded-lg flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-white text-sm font-semibold">Install Seafarer Logbook</p>
          {showIosGuide ? (
            <div className="text-slate-400 text-xs mt-1 space-y-1">
              <p>Add to your home screen for the full app experience:</p>
              <p className="flex items-center gap-1.5">
                1. Tap <Share className="h-3.5 w-3.5 inline text-cyan-400" /> <span className="font-medium text-slate-300">Share</span>
              </p>
              <p className="flex items-center gap-1.5">
                2. Tap <PlusSquare className="h-3.5 w-3.5 inline text-cyan-400" /> <span className="font-medium text-slate-300">Add to Home Screen</span>
              </p>
            </div>
          ) : (
            <p className="text-slate-400 text-xs mt-1">
              Get the full app experience — quick access from your home screen, works offline.
            </p>
          )}
          {!showIosGuide && (
            <Button
              size="sm"
              onClick={install}
              className="mt-3 bg-amber-600 hover:bg-amber-700 text-white h-8 text-xs"
              data-testid="install-app-btn"
            >
              <Download className="h-3.5 w-3.5 mr-1.5" />
              Install App
            </Button>
          )}
        </div>
        <button
          onClick={dismiss}
          className="text-slate-500 hover:text-slate-300 flex-shrink-0"
          data-testid="install-prompt-dismiss"
          aria-label="Dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};
